import { useMutation } from '@tanstack/react-query';
import type { AxiosError } from 'axios';
import { useContext, useEffect, useMemo, useRef, useState } from 'react';
import { Controller, useForm } from 'react-hook-form';
import { useNavigate } from 'react-router-dom';
import * as yup from 'yup';

import StaffSelect from '@/components/select/StaffSelect/StaffSelect';
import WorkGroupSelect from '@/components/select/WorkGroupSelect/WorkGroupSelect';
import TagSelector from '@/components/TagSelector/TagSelector';
import { Color } from '@/constants/color';
import itemStatuses from '@/constants/itemStatuses';
import { LocalStorageKeys } from '@/constants/localStorage';
import { taskable_type_person } from '@/constants/taskableTypes';
import AppContext from '@/contexts/appContext';
import { buildYupSchema, FormRenderer, toFieldDefs, type FormSchema } from '@/formBuilder';
import NextScheduledAppointmentAlert from '@/forms/TaskForm/components/NextScheduledAppointmentAlert';
import type { Props } from '@/forms/TaskForm/types';
import useAPI from '@/hooks/useAPI';
import useLocalStorage from '@/hooks/useLocalStorage';
import WorkRepository from '@/repositories/WorkRepository';
import type { ItemFormData, ItemPayload } from '@/types/item';
import type { Tag } from '@/types/tag';
import type { WorkGroup } from '@/types/workGroup';
import AutocompleteSingleSelect from '@/ui/components/AutocompleteSingleSelect';
import Badge from '@/ui/components/Badge/Badge';
import DatetimePicker from '@/ui/components/Form/DatetimePicker';
import InputField from '@/ui/components/Form/InputField/InputField';
import InputGroup from '@/ui/components/Form/InputGroup';
import Label from '@/ui/components/Form/Label';
import RichTextEditor from '@/ui/components/Form/RichTextEditor';
import { OptionType } from '@/ui/components/Form/Select/types';
import Spinner from '@/ui/components/Spinner';
import setStaffSelectValue from '@/utils/formatStaffAsSelectOption';
import setWorkGroupSelectValue from '@/utils/formatWorkGroupAsSelectOption';
import itemStatusName from '@/utils/itemStatusName';
import mapAxiosErrorMessages from '@/utils/mapAxiosErrorMessages';
import queryClient from '@/utils/testing/queryClient';

import FieldError from '../FieldError';

const TaskForm = ({ taskableId, taskableType, personId, item, returnTo }: Props) => {
  const { addMessage } = useContext(AppContext);
  const taskCreatedRef = useRef(false);
  const {
    removeValue: removeLocalStorageValue,
    setValue: setLocalStorageValue,
    storedValue
  } = useLocalStorage(`${LocalStorageKeys.NEW_TASK_DRAFT}_${taskableId}`);
  const contentTypeModel = taskableType.includes('.') ? taskableType.split('.')[1] : taskableType;
  const defaultStatus = 'open';

  const defaultFormValues = (): Partial<ItemFormData> => {
    if (item) {
      return {
        ...item,
        assigned_work_group: item.assigned_work_group
          ? setWorkGroupSelectValue(item.assigned_work_group)
          : undefined,
        assigned_user: item.assigned_user ? setStaffSelectValue(item.assigned_user) : undefined,
        blueprint_id: undefined,
        description: item?.description ?? '',
        tags:
          item.tags?.map((tag) => {
            return {
              label: tag.name,
              value: tag.id
            };
          }) || []
      };
    }

    if (storedValue) {
      return {
        ...storedValue,
        blueprint_id: undefined,
        description: storedValue.description
      };
    }

    return {
      assigned_work_group: undefined,
      assigned_user: undefined,
      blueprint_id: undefined,
      description: '',
      due_at: '',
      name: '',
      person_id: personId,
      snoozed_until: '',
      status: defaultStatus,
      tags: [],
      taskable: undefined,
      taskable_id: taskableId,
      taskable_type: taskableType
    };
  };

  const {
    control,
    formState: { errors },
    handleSubmit,
    register,
    setError,
    setValue,
    watch
  } = useForm({
    defaultValues: defaultFormValues()
  });

  const values = watch();
  const statusRegister = register('status');

  const [blueprintTags, setBlueprintTags] = useState<Tag[]>([]);
  const [formSchema, setFormSchema] = useState<FormSchema | null>(item?.form_schema ?? null);
  const formFieldDefs = useMemo(() => (formSchema ? toFieldDefs(formSchema) : []), [formSchema]);
  const formYupSchema = useMemo(() => buildYupSchema(formFieldDefs), [formFieldDefs]);

  const [, setBlueprintAssignedWorkGroup] = useState<WorkGroup | undefined>(undefined);
  const navigate = useNavigate();
  const workRepo = WorkRepository();
  const [loadingPreview, setLoadingPreview] = useState(false);

  // [TODO]: Update disableWorkGroupSelect logic when product
  // wants the feature enabled
  // const disableWorkGroupSelect = !!blueprintAssignedWorkGroup;

  const disableWorkGroupSelect = useMemo(() => {
    // If taskable is a person, disable the work group select
    return taskableType === taskable_type_person;
  }, [taskableType]);

  const { data: blueprints, isPending: loadingBlueprints } = useAPI({
    queryKey: ['getBlueprints', contentTypeModel],
    queryFn: async () => {
      return await workRepo.listBlueprints(contentTypeModel);
    }
  });

  const selectedBlueprintKey = useMemo(() => {
    if (!values.blueprint_id?.value || !blueprints?.data) return undefined;
    return blueprints.data.find((bp) => bp.id === values.blueprint_id?.value)?.key;
  }, [values.blueprint_id?.value, blueprints?.data]);

  useEffect(() => {
    if ((item?.blueprint_id || (!item && storedValue?.blueprint_id?.value)) && blueprints?.data) {
      const blueprintId = item?.blueprint_id || storedValue?.blueprint_id?.value;
      const match = blueprints.data.find((blueprint) => blueprint.id === blueprintId);

      if (match) {
        setValue('blueprint_id', {
          label: match.name,
          value: match.id
        });
      }
    }
  }, [item, blueprints?.data, setValue, storedValue?.blueprint_id?.value]);

  useEffect(() => {
    // If editing a task, fetch any read-only blueprint data
    // to display (like template tags)
    const fetchBlueprintData = async () => {
      try {
        const blueprintId = item?.blueprint_id || storedValue?.blueprint_id?.value;
        const response = await workRepo.previewItem(taskableId, taskableType, blueprintId);
        if (response.data?.blueprint_tags) setBlueprintTags(response.data.blueprint_tags);
        if (response.data?.form_schema) setFormSchema(response.data.form_schema);
      } catch (error) {
        addMessage(mapAxiosErrorMessages(error as AxiosError));
      }
    };

    if (item?.blueprint_id || (!item && storedValue?.blueprint_id?.value)) {
      void fetchBlueprintData();
    }
    // FIXME: workrepo
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [addMessage, item, storedValue?.blueprint_id?.value, taskableId, taskableType]);

  useEffect(() => {
    if (!item && !taskCreatedRef.current) {
      const valuesSubscription = watch((values) => {
        const serializedDescription = values.description || '';
        setLocalStorageValue({
          ...values,
          description: serializedDescription
        });
      });
      return () => {
        valuesSubscription.unsubscribe();
        if (taskCreatedRef.current) removeLocalStorageValue();
      };
    }
  }, [item, removeLocalStorageValue, setLocalStorageValue, watch]);

  // When the blueprint changes, fetch the blueprint preview and populate the form
  // [TODO]: Check form dirty state and prompt user to save changes before overwriting
  const handleBlueprintChange = async (
    option: OptionType<string> | null,
    onChange: (...event: unknown[]) => void
  ) => {
    const blueprintId = option?.value;

    if (blueprintId) {
      setBlueprintAssignedWorkGroup(undefined);
      setLoadingPreview(true);

      // Switching templates invalidates any answers already entered, since a
      // different form's fields won't match the keys they were captured under.
      setValue('form_data', {});
      setFormSchema(null);

      await workRepo
        .previewItem(taskableId, taskableType, blueprintId)
        .then((response) => {
          if (response.data) {
            const { data } = response;

            setFormSchema(data.form_schema ?? null);
            data.name && setValue('name', data.name);
            data?.description && setValue('description', data.description);
            data.status && setValue('status', data.status);
            data.due_at && setValue('due_at', data.due_at);
            data.snoozed_until && setValue('snoozed_until', data.snoozed_until);
            data.assigned_user &&
              setValue('assigned_user', setStaffSelectValue(data.assigned_user));
            data.blueprint_tags && setBlueprintTags(data.blueprint_tags);
            if (data?.assigned_work_group) {
              setValue('assigned_work_group', setWorkGroupSelectValue(data.assigned_work_group));
              setBlueprintAssignedWorkGroup(data.assigned_work_group);
            }
          }
        })
        .catch((error) => {
          addMessage(mapAxiosErrorMessages(error as AxiosError));
        });

      setLoadingPreview(false);

      onChange(option);
    }
  };

  const updateItem = useMutation({
    mutationFn: ({ id, values }: { id: string; values: Partial<ItemPayload> }) => {
      return workRepo.updateItem(id, values);
    },
    onSuccess: ({ data }) => {
      data && queryClient.invalidateQueries({ queryKey: ['getItem', data.id] });
      addMessage([{ level: 'success', text: 'Successfully updated task' }]);
      if (data?.id) {
        navigate(`/people/${personId}/tasks/${data?.id}`);
      } else {
        navigate(`/people/${personId}/profile`);
      }
    },
    onError: (error: AxiosError) => {
      addMessage(mapAxiosErrorMessages(error));
    }
  });

  const createItem = useMutation({
    mutationFn: ({ values }: { values: Partial<ItemPayload> }) => {
      return workRepo.createItem(values);
    },
    onSuccess: () => {
      taskCreatedRef.current = true;
      addMessage({ level: 'success', text: 'Successfully created task' });
      if (returnTo) {
        navigate(returnTo);
      } else {
        navigate(`/people/${personId}/profile`);
      }
    },
    onError: (error: AxiosError) => {
      addMessage(mapAxiosErrorMessages(error));
    }
  });

  const submitForm = () => {
    const { assigned_user, assigned_work_group, blueprint_id, description, tags } = values;

    // Remove assigned_user and assigned_work_group from payload. Instead send and the string
    delete values.assigned_user;
    delete values.assigned_work_group;

    const payload: Partial<ItemPayload> = {
      ...values,
      assigned_work_group_id: assigned_work_group ? assigned_work_group.value : undefined,
      assigned_user_id: assigned_user ? assigned_user.value : undefined,
      blueprint_id: blueprint_id ? blueprint_id.value : undefined,
      description: description || null,
      tags:
        tags?.map((t) => {
          return { id: t.value };
        }) ?? []
    };
    payload.due_at = payload.due_at === '' ? undefined : values.due_at;
    payload.snoozed_until = payload.snoozed_until === '' ? undefined : values.snoozed_until;

    // A blueprint without a form has nothing for form_data to answer, and the
    // backend rejects even an empty object as a claim of answers.
    if (!formSchema) {
      delete payload.form_data;
    }

    if (payload.id) {
      // Update an existing item
      updateItem.mutate({ id: payload.id, values: payload });
    } else {
      // Create a new item
      createItem.mutate({ values: payload });
    }
  };

  const isSubmitting = values.id ? updateItem.isPending : createItem.isPending;

  // The blueprint's form isn't wired into RHF's own validation (its fields are
  // dynamic per blueprint), so it's validated separately here and reported
  // back through setError so FormRenderer can show it inline like any other
  // field.
  const onSubmit = handleSubmit(async () => {
    if (formFieldDefs.length) {
      try {
        // Yup coerces strings like "900" to numbers only for the purpose of
        // this check; without assigning the result back, the payload below
        // would still carry the raw string RHF captured from the input.
        values.form_data = await formYupSchema.validate(values.form_data ?? {}, {
          abortEarly: false
        });
      } catch (validationError) {
        if (validationError instanceof yup.ValidationError) {
          validationError.inner.forEach((fieldError) => {
            if (!fieldError.path) return;
            // eslint-disable-next-line @typescript-eslint/no-explicit-any
            setError(`form_data.${fieldError.path}` as any, { message: fieldError.message });
          });
        }
        return;
      }
    }
    submitForm();
  });

  return (
    <form onSubmit={onSubmit}>
      <NextScheduledAppointmentAlert selectedBlueprintKey={selectedBlueprintKey} />

      {loadingBlueprints ? (
        <Spinner label="Loading templates..." />
      ) : (
        <InputGroup>
          {loadingPreview && <Spinner />}

          <Controller
            name="blueprint_id"
            control={control}
            render={({ field }) => {
              return (
                <AutocompleteSingleSelect
                  clearable={false}
                  label="Task Template *"
                  onChange={(option) => handleBlueprintChange(option, field.onChange)}
                  options={
                    blueprints?.data?.map((blueprint) => {
                      return {
                        label: blueprint.name,
                        value: blueprint.id
                      };
                    }) ?? []
                  }
                  placeholder="Select a Template"
                  selectedOption={field.value ? field.value : { label: '', value: '' }}
                />
              );
            }}
            rules={{ required: 'Task Template is required' }}
          />
          <FieldError message={errors.blueprint_id?.message} />
        </InputGroup>
      )}

      <InputGroup>
        <Controller
          name="name"
          control={control}
          render={({ field }) => {
            return (
              <InputField
                {...field}
                label="Name *"
                id="id-name"
                placeholder="Name"
                readOnly={
                  !!values.blueprint_id &&
                  blueprints?.data?.find((blueprint) => blueprint.id === values.blueprint_id?.value)
                    ?.name !== 'Custom'
                }
                type="text"
              />
            );
          }}
          rules={{ required: 'Name is required' }}
        />
        <FieldError message={errors.name?.message} />
      </InputGroup>

      <InputGroup>
        <Label htmlFor="id-description" text="Description">
          <Controller
            name="description"
            control={control}
            render={({ field }) => {
              return <RichTextEditor onChange={field.onChange} value={values.description} />;
            }}
          />
        </Label>
        <FieldError message={errors.description?.message} />
      </InputGroup>

      <InputGroup>
        <Label htmlFor="id-status" text="Status">
          <select
            {...statusRegister}
            onChange={(event) => {
              void statusRegister.onChange(event);
              if (event.currentTarget.value !== 'snoozed') {
                setValue('snoozed_until', '');
              }
            }}
            className="mt-1 block w-full py-2 px-3 border border-gray-300 bg-white rounded-md shadow-xs focus:outline-hidden focus:ring-indigo-500 focus:border-indigo-500 text-base"
            id="id-status">
            {itemStatuses.map((option) => {
              return (
                <option key={option} value={option}>
                  {itemStatusName(option)}
                </option>
              );
            })}
          </select>
        </Label>
        <FieldError message={errors.status?.message} />
      </InputGroup>

      {values.status === 'snoozed' && (
        <InputGroup>
          <Controller
            name="snoozed_until"
            control={control}
            render={({ field }) => {
              return (
                <DatetimePicker
                  disabled={values.status !== 'snoozed'}
                  onBlur={field.onBlur}
                  onChange={field.onChange}
                  label="Snoozed Until"
                  name={field.name}
                  value={field.value}
                />
              );
            }}
            rules={{
              validate: (value) => {
                return values.status === 'snoozed' && !value
                  ? 'Required when status is snoozed'
                  : true;
              }
            }}
          />
          <FieldError message={errors.snoozed_until?.message} />
        </InputGroup>
      )}

      <InputGroup>
        <Controller
          name="due_at"
          control={control}
          render={({ field }) => {
            return (
              <DatetimePicker
                onBlur={field.onBlur}
                onChange={field.onChange}
                name={field.name}
                label="Due At"
                value={field.value}
              />
            );
          }}
        />
        <FieldError message={errors.due_at?.message} />
      </InputGroup>

      {values.taskable_type != taskable_type_person && (
        <InputGroup>
          <Controller
            name="assigned_user"
            control={control}
            render={({ field }) => {
              return (
                <StaffSelect
                  label="Assigned User"
                  onChange={field.onChange}
                  placeholder="Select Assigned User"
                  selectedOption={field.value ? field.value : { label: '', value: '' }}
                />
              );
            }}
          />
          <FieldError message={errors.assigned_user?.message} />
        </InputGroup>
      )}

      <InputGroup>
        <Controller
          name="assigned_work_group"
          control={control}
          render={({ field }) => {
            return (
              <WorkGroupSelect
                disabled={disableWorkGroupSelect}
                label="Assigned Group"
                onChange={field.onChange}
                placeholder="Select Assigned Group"
                selectedOption={field.value ? field.value : { label: '', value: '' }}
              />
            );
          }}
        />
        <FieldError message={errors.assigned_work_group?.message} />
      </InputGroup>

      {!!blueprintTags.length && (
        <InputGroup>
          <Label htmlFor="" text="Template Tags" />
          <div className="flex flex-wrap gap-x-1 gap-y-1">
            {blueprintTags.map((tag) => {
              return <Badge key={tag.name} color={Color.GRAY} text={tag.name} />;
            })}
          </div>
        </InputGroup>
      )}

      <InputGroup>
        <Controller
          name="tags"
          control={control}
          render={({ field }) => {
            return (
              <TagSelector
                contentType="items"
                onChange={field.onChange}
                name={field.name}
                value={field.value}
              />
            );
          }}
        />
        <FieldError message={errors.tags?.message} />
      </InputGroup>

      {formSchema && (
        <InputGroup>
          <Label htmlFor="" text="Form" />
          <FormRenderer schema={formSchema} control={control} namespace="form_data" />
        </InputGroup>
      )}

      <InputGroup>
        <button
          disabled={isSubmitting}
          type="submit"
          className="w-full px-4 py-2 text-base font-semibold text-center text-white transition duration-200 ease-in bg-indigo-600 rounded-lg shadow-md hover:bg-indigo-700 focus:outline-hidden focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500">
          {isSubmitting ? (
            <Spinner color={Color.WHITE} label="Submitting..." />
          ) : (
            `${values.id ? 'Update' : 'Create'} Task`
          )}
        </button>
      </InputGroup>
    </form>
  );
};

export default TaskForm;
